import React, { createContext, useContext, useEffect } from "react";
import { makeGetEntities } from "../redux/selectors/selectors";
import { useDispatch, useSelector } from "react-redux";
import { fetchCourseCtoa } from "../redux/actions/userAssignmentsListActions";
import { setExpandedList } from "../redux/actions/learningCenter/expandedAssignmentsActions";
import { forOwn } from "lodash";
import { courseSectionTypes, statusCompleted } from "../constants";
import { CourseCardExpansionPanelItem } from "../components/CourseCard";
import UserAssignmentContext from "./UserAssignmentContext";
import { Box, Divider } from "@mui/material";
import Skeleton from "../components/Skeleton/Skeleton";

const ActivitiesSkeleton = ({}) => {
  const listSize = 3;
  return [...Array(listSize)].map((v, index) => (
    <Box key={index} display="flex" flexDirection="column">
      <Box
        display="flex"
        justifyContent="space-between"
        height={48}
        alignItems="center"
      >
        <Box display="flex" flex={1}>
          <Skeleton variant="circular" height={24} width={24} mr={4} />
          <Skeleton width="75%" height={20} />
        </Box>
        <Box display="flex">
          <Skeleton variant="circular" height={24} width={24} mr={3} />
          <Skeleton width={66} height={20} />
        </Box>
      </Box>
      {index !== listSize - 1 && <Divider sx={{ ml: 7 }} />}
    </Box>
  ));
};

const selector = makeGetEntities();

const ExpandedAssignmentsContext = createContext();

const allActivitiesCompleted = activities =>
  activities.every(activity => activity.status === statusCompleted);

const ExpandedAssignmentsProvider = ({ children }) => {
  const dispatch = useDispatch();
  const { onAddToCalendarClick } = useContext(UserAssignmentContext);

  const { expandedAssignments, assignments } = useSelector(
    ({ expandedAssignmentsState, userAssignmentsListState }) => ({
      expandedAssignments: expandedAssignmentsState.expandedList,
      assignments: selector(userAssignmentsListState, {
        type: "userAssignment"
      })
    })
  );

  useEffect(() => {
    forOwn(expandedAssignments, (expanded, assignmentId) => {
      if (expanded) {
        const expandedAssignment = assignments.find(
          ({ id }) => `${id}` === assignmentId
        );
        if (expandedAssignment && expandedAssignment.enrolledCtoaId) {
          dispatch(
            fetchCourseCtoa(
              expandedAssignment.assignedTrainingObjectId,
              expandedAssignment.id
            )
          );
        }
      }
    });
  }, [expandedAssignments]);

  const isAssignmentExpanded = assignment => {
    return assignment && expandedAssignments[assignment.id];
  };

  const handleExpandCourseCard = (expanded, assignment) => {
    dispatch(setExpandedList(assignment.id, expanded));
  };

  const alwaysShouldBeExpanded = userAssignment => {
    return (
      userAssignment?.enrolledCtoaId &&
      !!userAssignment?.iltInfo &&
      userAssignment?.totalActivities === 0
    );
  };

  const getExpansionPanelItems = userAssignment => {
    const courseSections = userAssignment?.courseSections
      ? userAssignment?.courseSections?.filter(
          courseSection =>
            courseSection.sectionType == courseSectionTypes.mandatory
        )
      : userAssignment?.courseSections;

    return courseSections ? (
      userAssignment.courseSections.map((courseSection, index) => (
        <CourseCardExpansionPanelItem
          title={courseSection.title}
          timeEstimate={courseSection.timeEstimate}
          completed={allActivitiesCompleted(courseSection.courseContentables)}
          completedScore={courseSection.completedScore}
          key={courseSection.id}
          lastItem={courseSections.length - 1 === index}
          onAddToCalendarClick={() =>
            onAddToCalendarClick(userAssignment, courseSection, true)
          }
        />
      ))
    ) : userAssignment?.totalActivities ? (
      <ActivitiesSkeleton />
    ) : (
      []
    );
  };
  const contextValue = {
    isAssignmentExpanded,
    onExpandCourseCard: handleExpandCourseCard,
    alwaysShouldBeExpanded,
    getExpansionPanelItems,
    expandedAssignments
  };

  return (
    <ExpandedAssignmentsContext.Provider value={contextValue}>
      {children}
    </ExpandedAssignmentsContext.Provider>
  );
};

export default ExpandedAssignmentsContext;

export { ExpandedAssignmentsProvider };
